/**
 * Break Detectors — two agents watching the same customers at different grain.
 *
 * The page exists to answer two questions a table cannot: *what kind of break
 * is this*, and *does the order history actually look broken*. So every section
 * says in words what it is watching, and every row opens into the account's own
 * 52-week rhythm.
 *
 * The rule that shapes the layout: **A1 and A2 describe the same events at
 * different grain and their dollars must never be added.** A Starbucks network
 * break in A1 has its ship-to findings underneath it in A2; summing both double
 * counts by the size of the largest break. That is why they are two sections
 * with two totals rather than one list with a type column, and why the header
 * says so rather than leaving it to a footnote.
 *
 * Nothing is computed here. Every figure, including the cadence the chart draws
 * an expectation from, arrives in the payload.
 */

import { Fragment, useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

import { BreakTimeline, type OrderPoint } from './BreakTimeline'

const NONE = '—'
const YES = 'yes'

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function gal(value: number | null): string {
  return value === null ? NONE : Math.round(value).toLocaleString('en-US')
}

function money(value: number | null): string {
  return value === null ? NONE : `$${Math.round(value).toLocaleString('en-US')}`
}

function days(value: number | null): string {
  return value === null ? NONE : `${String(Math.round(value))}d`
}

/** One line inside an account, when the chart is drawing a dropout. */
interface LineContext {
  readonly line: string
  readonly lastOrder: string | null
  readonly daysSilent: number | null
  readonly cyclesMissed: number | null
}

/** The key a row is selected by — the pair grain, which is what a break is. */
function pairKey(row: Keyed): string {
  return `${text(row['end_user']) ?? ''}::${text(row['ship_to_number']) ?? ''}`
}

export function BreakDetectors({ result }: CustomReportProps) {
  const networkBreaks = dataset(result, 'network_breaks')
  const networkChildren = dataset(result, 'network_children')
  const accountBreaks = dataset(result, 'account_breaks')
  const totals = dataset(result, 'totals')
  const lostSummary = dataset(result, 'lost_summary')[0]
  const asOf = dataset(result, 'as_of')
  const orderHistory = dataset(result, 'order_history')
  const accountCadence = dataset(result, 'account_cadence')
  const lineDropouts = dataset(result, 'line_dropouts')

  const [openAccount, setOpenAccount] = useState<string | null>(null)
  const [openNetwork, setOpenNetwork] = useState<string | null>(null)
  const [openLine, setOpenLine] = useState<string | null>(null)

  const cadenceFor = (key: string): Keyed | undefined =>
    accountCadence.find((row) => pairKey(row) === key)

  /**
   * Orders for one account, or for one line inside it.
   *
   * History arrives per (account, line, date). A pair chart sums the lines back
   * together for each date — otherwise an account buying four lines would draw
   * four bars a day and read as four times the cadence. A line chart keeps only
   * that line, which is the whole point of an A5 finding.
   */
  const ordersFor = (key: string, line?: string): OrderPoint[] => {
    const matching = orderHistory.filter(
      (row) => pairKey(row) === key && (line === undefined || text(row['hist_product_line']) === line),
    )
    const byDate = new Map<string, OrderPoint>()
    for (const row of matching) {
      const date = text(row['hist_order_date']) ?? ''
      if (date === '') continue
      const existing = byDate.get(date)
      const gallons = (existing?.gallons ?? 0) + (num(row['hist_gallons']) ?? 0)
      byDate.set(date, {
        date,
        gallons,
        isOpen: (existing?.isOpen ?? false) || text(row['hist_is_open']) === 'true',
      })
    }
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date))
  }

  const toggleAccount = (key: string) => {
    setOpenAccount((current) => (current === key ? null : key))
  }

  /** The chart plus the model behind it, for one account or one of its lines. */
  const timelineFor = (key: string, line?: LineContext) => {
    const cadence = cadenceFor(key)
    const lastOrder = line?.lastOrder ?? text(cadence?.['last_order_date']) ?? null
    const silent = line?.daysSilent ?? num(cadence?.['days_silent'])
    const cycles = line?.cyclesMissed ?? num(cadence?.['cycles_missed'])
    return (
      <div className="bd-detail">
        <BreakTimeline
          orders={ordersFor(key, line?.line)}
          lastOrder={lastOrder}
          measuredTo={text(cadence?.['measured_to']) ?? null}
          expectedInterval={num(cadence?.['expected_interval'])}
          medianOrder={num(cadence?.['median_order_gal'])}
          daysSilent={silent}
          cyclesMissed={cycles}
          unproven={text(cadence?.['unproven']) === YES}
        />
        <dl className="bd-facts">
          <div>
            <dt>Cadence</dt>
            <dd>{text(cadence?.['cadence_class']) ?? NONE}</dd>
          </div>
          <div>
            <dt>Expected every</dt>
            <dd>{days(num(cadence?.['expected_interval']))}</dd>
          </div>
          <div>
            <dt>Median order</dt>
            <dd>{gal(num(cadence?.['median_order_gal']))} gal</dd>
          </div>
          <div>
            <dt>Orders 26wk</dt>
            <dd>{count(cadence?.['orders_26wk'])}</dd>
          </div>
          <div>
            <dt>Last order</dt>
            <dd>{text(cadence?.['last_order_date']) ?? NONE}</dd>
          </div>
          <div>
            <dt>First order</dt>
            <dd>{text(cadence?.['first_order_date']) ?? NONE}</dd>
          </div>
        </dl>
      </div>
    )
  }

  return (
    <div className="bespoke-report bd-page">
      <section className="bd-intro">
        <h2 className="bd-intro-title">What this report watches</h2>
        <p className="bd-intro-body">
          Two agents look for customers who have stopped ordering, at two different grains.
          They describe the same events, so their figures are kept apart:{' '}
          <strong>never add the two dollar totals together</strong> — a network break in A1
          has its own ship-to findings underneath it in A2, and summing both double counts by
          the size of the largest break.
        </p>
        <div className="bd-totals">
          {totals.map((row) => (
            <article className="bd-total" key={text(row['agent']) ?? ''}>
              <h3 className="bd-total-title">{text(row['agent']) ?? NONE}</h3>
              <p className="bd-total-watches">{text(row['watches']) ?? ''}</p>
              <p className="bd-total-figure cell-number">{count(row['findings'])}</p>
              <p className="bd-total-meta">
                {gal(num(row['gallons_at_risk']))} gal · {money(num(row['dollars_at_risk']))} at
                risk
              </p>
              <p className="bd-total-note">{text(row['note']) ?? ''}</p>
            </article>
          ))}
        </div>
      </section>

      {lostSummary !== undefined && count(lostSummary['accounts_lost']) > 0 && (
        <section className="bd-lost">
          <p className="bd-lost-head">
            Separately, {count(lostSummary['accounts_lost']).toLocaleString()} accounts across{' '}
            {count(lostSummary['customers_lost']).toLocaleString()} customers have been silent{' '}
            {count(lostSummary['threshold_days'])} days or more —{' '}
            <b>{gal(num(lostSummary['gallons_lost']))} gal</b>
            {text(lostSummary['biggest']) === null
              ? ''
              : `, the largest being ${text(lostSummary['biggest']) ?? ''}`}
            .
          </p>
          <p className="bd-lost-note">
            These are counted as lost, not broken, and are kept out of the breaks above so a
            customer who left months ago stops arriving as this week&rsquo;s news. Choose{' '}
            <b>Presumed lost</b> in the Account status filter to review them.
          </p>
        </section>
      )}

      <Card
        title="Network breaks — A1"
        subtitle={
          'One customer, several of its ship-tos going quiet together. A correlated stop is ' +
          'a different problem from one depot pausing: it points at the customer, not the ' +
          'account. Open a row to see which ship-tos stopped and when.'
        }
      >
        {networkBreaks.length === 0 ? (
          <Empty label="No open network breaks." />
        ) : (
          <div className="bd-scroll">
            <table className="bd-table">
              <thead>
                <tr>
                  <th scope="col" className="bd-col-name">
                    Customer
                  </th>
                  <th scope="col">Severity</th>
                  <th scope="col" className="cell-number">
                    Ship-tos silent
                  </th>
                  <th scope="col" className="cell-number">
                    Of total
                  </th>
                  <th scope="col" className="cell-number">
                    Volume share
                  </th>
                  <th scope="col" className="cell-number">
                    Spread
                  </th>
                  <th scope="col">Simultaneous</th>
                  <th scope="col" className="cell-number">
                    Gal at risk
                  </th>
                </tr>
              </thead>
              <tbody>
                {networkBreaks.map((row) => {
                  const customer = text(row['end_user']) ?? ''
                  const isOpen = openNetwork === customer
                  const children = networkChildren.filter(
                    (child) => text(child['end_user']) === customer,
                  )
                  return (
                    <Fragment key={customer}>
                      <tr className={isOpen ? 'bd-parent is-open' : 'bd-parent'}>
                        <td className="bd-col-name">
                          <button
                            type="button"
                            className="bd-disclose"
                            aria-expanded={isOpen}
                            onClick={() => {
                              setOpenNetwork((current) =>
                                current === customer ? null : customer,
                              )
                            }}
                          >
                            <span className="bd-caret" aria-hidden="true">
                              {isOpen ? '▾' : '▸'}
                            </span>
                            {customer}
                          </button>
                        </td>
                        <td>{text(row['severity']) ?? NONE}</td>
                        <td className="cell-number">{count(row['shiptos_silent'])}</td>
                        <td className="cell-number">{count(row['shiptos_total'])}</td>
                        <td className="cell-number">
                          {num(row['volume_share_pct']) === null
                            ? NONE
                            : `${(num(row['volume_share_pct']) ?? 0).toFixed(1)}%`}
                        </td>
                        <td className="cell-number">{days(num(row['spread_days']))}</td>
                        <td>{text(row['simultaneous']) ?? NONE}</td>
                        <td className="cell-number">{gal(num(row['gallons_at_risk']))}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bd-expansion">
                          <td colSpan={8}>
                            <p className="bd-interpretation">
                              {text(row['interpretation']) ?? text(row['title']) ?? ''}
                            </p>
                            <table className="bd-table bd-subtable">
                              <thead>
                                <tr>
                                  <th scope="col" className="bd-col-name">
                                    Ship-to
                                  </th>
                                  <th scope="col">Metro</th>
                                  <th scope="col">Last order</th>
                                  <th scope="col" className="cell-number">
                                    Silent
                                  </th>
                                  <th scope="col">Cadence</th>
                                  <th scope="col" className="cell-number">
                                    Gal TTM
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {children.map((child) => {
                                  const key = pairKey(child)
                                  const childOpen = openAccount === key
                                  return (
                                    <Fragment key={key}>
                                      <tr>
                                        <td className="bd-col-name">
                                          <button
                                            type="button"
                                            className="bd-disclose"
                                            aria-expanded={childOpen}
                                            onClick={() => {
                                              toggleAccount(key)
                                            }}
                                          >
                                            <span className="bd-caret" aria-hidden="true">
                                              {childOpen ? '▾' : '▸'}
                                            </span>
                                            {text(child['ship_to_number']) ?? NONE}
                                          </button>
                                        </td>
                                        <td>{text(child['cbsa_title']) ?? NONE}</td>
                                        <td>{text(child['last_order_date']) ?? NONE}</td>
                                        <td className="cell-number">
                                          {days(num(child['days_silent']))}
                                        </td>
                                        <td>{text(child['cadence_class']) ?? NONE}</td>
                                        <td className="cell-number">
                                          {gal(num(child['child_gallons_ttm']))}
                                        </td>
                                      </tr>
                                      {childOpen && (
                                        <tr className="bd-expansion">
                                          <td colSpan={6}>{timelineFor(key)}</td>
                                        </tr>
                                      )}
                                    </Fragment>
                                  )
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Account breaks — A2"
        subtitle={
          'One customer at one ship-to, measured against its own ordering rhythm. A silence ' +
          'break means the next order never came; a volume break means the orders came but ' +
          'got smaller. Click any row for its 52-week order history.'
        }
      >
        {accountBreaks.length === 0 ? (
          <Empty label="No open account breaks." />
        ) : (
          <div className="bd-scroll">
            <table className="bd-table">
              <thead>
                <tr>
                  <th scope="col" className="bd-col-name">
                    Customer · ship-to
                  </th>
                  <th scope="col">Kind</th>
                  <th scope="col">Severity</th>
                  <th scope="col">Last order</th>
                  <th scope="col" className="cell-number">
                    Silent
                  </th>
                  <th scope="col" className="cell-number">
                    Cycles missed
                  </th>
                  <th scope="col" className="cell-number">
                    Gal at risk
                  </th>
                  <th scope="col">In network break</th>
                </tr>
              </thead>
              <tbody>
                {accountBreaks.map((row) => {
                  const key = pairKey(row)
                  const isOpen = openAccount === key
                  return (
                    <Fragment key={key}>
                      <tr className={isOpen ? 'bd-parent is-open' : 'bd-parent'}>
                        <td className="bd-col-name">
                          <button
                            type="button"
                            className="bd-disclose"
                            aria-expanded={isOpen}
                            onClick={() => {
                              toggleAccount(key)
                            }}
                          >
                            <span className="bd-caret" aria-hidden="true">
                              {isOpen ? '▾' : '▸'}
                            </span>
                            <span>
                              <strong>{text(row['end_user']) ?? NONE}</strong>
                              <span className="bd-sub"> · {text(row['ship_to_number']) ?? NONE}</span>
                            </span>
                          </button>
                        </td>
                        <td>{text(row['break_kind']) ?? NONE}</td>
                        <td>{text(row['severity']) ?? NONE}</td>
                        <td>{text(row['last_order_date']) ?? NONE}</td>
                        <td className="cell-number">{days(num(row['days_silent']))}</td>
                        <td className="cell-number">
                          {num(row['spread_days']) === null
                            ? NONE
                            : (num(row['spread_days']) ?? 0).toFixed(1)}
                        </td>
                        <td className="cell-number">{gal(num(row['gallons_at_risk']))}</td>
                        <td>
                          {text(row['in_network_break']) === YES ? (
                            <span className="bd-flag">also in an A1 break</span>
                          ) : (
                            ''
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bd-expansion">
                          <td colSpan={8}>
                            <p className="bd-interpretation">{text(row['title']) ?? ''}</p>
                            {timelineFor(key)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="Line dropouts — A5"
        subtitle={
          'A product line that stopped at an account that is still ordering. Neither of the ' +
          'panels above can see these: the account keeps its cadence, so nothing at account ' +
          'grain ever goes silent and the line just disappears from the mix. Ranked by how ' +
          'much of the account the line used to be. Click a row for that line\'s own history.'
        }
      >
        {lineDropouts.length === 0 ? (
          <Empty label="No lines have dropped at an account still ordering." />
        ) : (
          <div className="bd-scroll">
            <table className="bd-table">
              <thead>
                <tr>
                  <th scope="col" className="bd-col-name">
                    Customer · ship-to
                  </th>
                  <th scope="col">Line</th>
                  <th scope="col" className="cell-number">
                    Line silent
                  </th>
                  <th scope="col" className="cell-number">
                    Account silent
                  </th>
                  <th scope="col" className="cell-number">
                    Share of account
                  </th>
                  <th scope="col" className="cell-number">
                    Cycles missed
                  </th>
                  <th scope="col" className="cell-number">
                    Gal at risk
                  </th>
                  <th scope="col">Line cadence</th>
                </tr>
              </thead>
              <tbody>
                {lineDropouts.map((row) => {
                  const key = pairKey(row)
                  const line = text(row['product_line']) ?? ''
                  const rowKey = `${key}::${line}`
                  const isOpen = openLine === rowKey
                  const share = num(row['share_of_pair_pct'])
                  return (
                    <Fragment key={rowKey}>
                      <tr className={isOpen ? 'bd-parent is-open' : 'bd-parent'}>
                        <td className="bd-col-name">
                          <button
                            type="button"
                            className="bd-disclose"
                            aria-expanded={isOpen}
                            onClick={() => {
                              setOpenLine((current) => (current === rowKey ? null : rowKey))
                            }}
                          >
                            <span className="bd-caret" aria-hidden="true">
                              {isOpen ? '▾' : '▸'}
                            </span>
                            <span>
                              <strong>{text(row['end_user']) ?? NONE}</strong>
                              <span className="bd-sub"> · {text(row['ship_to_number']) ?? NONE}</span>
                            </span>
                          </button>
                        </td>
                        <td>
                          <span className="bd-line">{line || NONE}</span>
                        </td>
                        <td className="cell-number bd-strong">{days(num(row['days_silent']))}</td>
                        <td className="cell-number">{days(num(row['pair_days_silent']))}</td>
                        <td className="cell-number">
                          {share === null ? NONE : `${share.toFixed(1)}%`}
                        </td>
                        <td className="cell-number">
                          {num(row['cycles_missed']) === null
                            ? NONE
                            : (num(row['cycles_missed']) ?? 0).toFixed(1)}
                        </td>
                        <td className="cell-number">{gal(num(row['gallons_at_risk']))}</td>
                        <td>{text(row['cadence_class']) ?? NONE}</td>
                      </tr>
                      {isOpen && (
                        <tr className="bd-expansion">
                          <td colSpan={8}>
                            <p className="bd-interpretation">{text(row['title']) ?? ''}</p>
                            <p className="bd-linenote">
                              This chart is <strong>{line}</strong> only, at{' '}
                              {text(row['ship_to_number']) ?? NONE}. The account last ordered{' '}
                              {text(row['pair_last_order_date']) ?? NONE}
                              {' — '}
                              {gal(num(row['line_gallons_ttm']))} of its{' '}
                              {gal(num(row['pair_gallons_ttm']))} gal a year came from this line,
                              across {count(row['lines_at_pair'])} lines in total.
                            </p>
                            {timelineFor(key, {
                              line,
                              lastOrder: text(row['last_order_date']) ?? null,
                              daysSilent: num(row['days_silent']),
                              cyclesMissed: num(row['cycles_missed']),
                            })}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="As of — what each figure was measured from"
        subtitle="A1 and A2 run on their own clocks, and a newer run is not newer data."
      >
        <table className="bd-table bd-asof">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">As of</th>
              <th scope="col">Age</th>
              <th scope="col">What it means</th>
            </tr>
          </thead>
          <tbody>
            {asOf.map((row) => (
              <tr key={text(row['source']) ?? ''}>
                <td>{text(row['source']) ?? NONE}</td>
                <td className="cell-number">{text(row['as_of']) ?? NONE}</td>
                <td>{text(row['age']) ?? ''}</td>
                <td className="bd-note">{text(row['note']) ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
